import { Link } from "react-router-dom";
import { Inbox, Loader, CheckCircle2, AlarmClock, AlertTriangle, Plus } from "lucide-react";
import { useApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, PageHead, Loading, Overline, Btn, Empty } from "../components/kit";
import { fmtDate, num, CATEGORY_LABEL, ROLE_LABEL } from "../lib/constants";

function Kpi({ label, value, icon: Icon, tone, testid }) {
  return (
    <Card className={`fade-up ${tone}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <Overline>{label}</Overline>
        <Icon className="w-5 h-5 opacity-70" />
      </div>
      <div className="mt-3 text-4xl font-extrabold font-mono tabular-nums">{value}</div>
    </Card>
  );
}

function LateList({ list }) {
  return (
    <Card data-testid="late-orders-card">
      <div className="flex items-center gap-2 mb-3"><AlarmClock className="w-5 h-5 text-rose-600" /><h3 className="font-bold text-lg">Order Telat</h3></div>
      {list.length === 0 ? <p className="text-sm text-slate-500">Tidak ada order yang telat.</p> : (
        <div className="divide-y divide-slate-100">
          {list.map((o) => (
            <Link key={o.id} to={`/order/${o.id}`} className="flex justify-between py-3 gap-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg" data-testid={`late-order-${o.order_no}`}>
              <div className="min-w-0">
                <div className="font-mono text-sm font-bold">{o.order_no}</div>
                <div className="text-sm text-slate-600 truncate">{o.customer?.name} · {num(o.total_qty)} pcs</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs font-bold text-rose-600">Deadline {fmtDate(o.deadline)}</div>
                <div className="text-xs text-slate-500">{o.status}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}

function LowStock({ list }) {
  return (
    <Card data-testid="low-stock-card">
      <div className="flex items-center gap-2 mb-3"><AlertTriangle className="w-5 h-5 text-amber-500" /><h3 className="font-bold text-lg">Stok Menipis</h3></div>
      {list.length === 0 ? <p className="text-sm text-slate-500">Semua stok aman.</p> : (
        <div className="divide-y divide-slate-100">
          {list.map((i) => (
            <Link key={i.id} to={`/stok/${i.id}`} className="flex justify-between py-3 gap-3" data-testid={`low-stock-${i.id}`}>
              <div className="min-w-0"><div className="font-semibold truncate">{i.name}</div><div className="text-xs text-slate-500">{CATEGORY_LABEL[i.category]}</div></div>
              <div className="text-right font-mono text-sm"><span className="font-bold text-rose-600">{num(i.stock)}</span><span className="text-slate-400"> / min {num(i.min_stock)} {i.unit}</span></div>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data } = useApi("/dashboard");
  if (!data) return <Loading />;
  const isGudang = user.role === "gudang";
  return (
    <>
      <PageHead title={`Halo, ${user.name}`} sub={`${ROLE_LABEL[user.role]} · Ringkasan produksi hari ini`}
        action={user.role === "admin" && <Link to="/order/baru"><Btn data-testid="dashboard-new-order-btn"><Plus className="w-5 h-5" /> <span className="hidden sm:inline">Order Baru</span></Btn></Link>} />
      {!isGudang && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <Kpi label="Order Hari Ini" value={data.today_orders} icon={Inbox} testid="kpi-today" />
          <Kpi label="Belum Selesai" value={data.open_orders} icon={Loader} testid="kpi-open" />
          <Kpi label="Selesai Bulan Ini" value={data.done_this_month} icon={CheckCircle2} tone="!bg-emerald-50 !border-emerald-200 text-emerald-800" testid="kpi-done-month" />
          <Kpi label="Order Telat" value={data.late_orders} icon={AlarmClock} tone={data.late_orders ? "!bg-rose-50 !border-rose-300 text-rose-700" : ""} testid="kpi-late" />
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-4">
        {!isGudang && <LateList list={data.late_list} />}
        {user.role !== "operator" && <LowStock list={data.low_stock} />}
      </div>
      {user.role === "operator" && data.open_orders === 0 && <Empty text="Belum ada order yang ditugaskan kepada Anda." data-testid="operator-empty" />}
    </>
  );
}
